// everything about how chix looks beyond the theme and pattern: colours, shape, type, atmosphere, layout.
// each person's own. the server stores it, cleanLook makes sure whatever comes in is something the page can paint

import { CURSOR_PACKS } from "./cursors";
import { DEFAULT_PATTERN_ID, DEFAULT_THEME_ID, THEMES, patternById, themeById } from "./themes";

export interface Choice {
  id: string;
  label: string;
  // shown as the button's tooltip and read out to screen readers
  hint?: string;
  // the section a long list groups this under, like fonts by kind
  group?: string;
  // only offered once this person has turned on mature options in Setup > Look
  mature?: boolean;
}

export const CORNERS: Choice[] = [
  { id: "cut", label: "cut", hint: "one clipped corner, the original look" },
  { id: "sharp", label: "sharp", hint: "square corners everywhere" },
  { id: "soft", label: "soft", hint: "gently rounded" },
  { id: "round", label: "round", hint: "very round, like a handheld menu" }
];

// bundled files or what the device already has, the page's csp only allows fonts from itself
export interface FontChoice extends Choice {
  display: string;
  label2: string;
  body: string;
}

const same = (family: string): Pick<FontChoice, "display" | "label2" | "body"> => ({ display: family, label2: family, body: family });

export const FONTS: FontChoice[] = [
  { id: "signal", group: "basic", label: "signal", hint: "the original: Rajdhani titles, pixel labels", display: "", label2: "", body: "" },
  { id: "clean", group: "basic", label: "clean", hint: "your device's own system font", ...same('system-ui, "Segoe UI", Roboto, "Helvetica Neue", sans-serif') },
  { id: "readable", group: "basic", label: "easy to read", hint: "Atkinson Hyperlegible, made for low vision", ...same('"Atkinson Hyperlegible", system-ui, sans-serif') },
  { id: "dyslexia", group: "basic", label: "dyslexia friendly", hint: "OpenDyslexic, weighted letter bottoms", ...same('"OpenDyslexic", "Atkinson Hyperlegible", system-ui, sans-serif') },
  { id: "rounded", group: "rounded", label: "rounded", hint: "Fredoka, soft and friendly", ...same('"Fredoka", system-ui, sans-serif') },
  { id: "serif", group: "serif", label: "serif", hint: "Lora titles and text", display: '"Lora", Georgia, "Times New Roman", serif', label2: '"Rajdhani", system-ui, sans-serif', body: '"Lora", Georgia, "Times New Roman", serif' },
  { id: "mono", group: "mono", label: "mono", hint: "Space Mono everywhere", ...same('"Space Mono", ui-monospace, "Cascadia Mono", Menlo, Consolas, monospace') },
  { id: "typewriter", group: "retro", label: "typewriter", hint: "Special Elite, inky and uneven", ...same('"Special Elite", "Courier New", ui-monospace, monospace') },
  { id: "hand", group: "handwritten", label: "handwritten", hint: "Caveat titles, Patrick Hand text", display: '"Caveat", "Patrick Hand", "Comic Sans MS", cursive', label2: '"Patrick Hand", "Comic Sans MS", cursive', body: '"Patrick Hand", "Comic Sans MS", cursive' },
  { id: "pixel", group: "pixel", label: "pixel", hint: "DotGothic16, a psx menu face", ...same('"DotGothic16", "Yu Gothic", ui-monospace, monospace') },
  { id: "terminal", group: "pixel", label: "terminal", hint: "VT323, a green screen console", ...same('"VT323", ui-monospace, monospace') },
  { id: "arcade", group: "pixel", label: "arcade", hint: "Press Start 2P titles, pixel text", display: '"Press Start 2P", "DotGothic16", monospace', label2: '"DotGothic16", "Yu Gothic", monospace', body: '"DotGothic16", "Yu Gothic UI", ui-monospace, monospace' },
  { id: "scifi", group: "sci-fi", label: "sci-fi", hint: "Orbitron titles over the original labels", display: '"Orbitron", "Rajdhani", system-ui, sans-serif', label2: "", body: "" },
  { id: "silkscreen", group: "pixel", label: "silkscreen", hint: "Silkscreen titles over the pixel labels", display: "\"Silkscreen\", ui-monospace, monospace", label2: "\"DotGothic16\", \"Yu Gothic\", monospace", body: "\"DotGothic16\", \"Yu Gothic UI\", ui-monospace, monospace" },
  { id: "pixelify", group: "pixel", label: "pixelify", hint: "Pixelify Sans, a chunky bitmap face", display: "\"Pixelify Sans\", ui-monospace, monospace", label2: "\"Pixelify Sans\", ui-monospace, monospace", body: "\"Pixelify Sans\", ui-monospace, monospace" },
  { id: "techmono", group: "mono", label: "tech mono", hint: "Share Tech Mono, a thin hud readout", display: "\"Share Tech Mono\", ui-monospace, monospace", label2: "\"Share Tech Mono\", ui-monospace, monospace", body: "\"Share Tech Mono\", ui-monospace, monospace" },
  { id: "plexmono", group: "mono", label: "plex mono", hint: "IBM Plex Mono, a tidy code face", display: "\"IBM Plex Mono\", ui-monospace, monospace", label2: "\"IBM Plex Mono\", ui-monospace, monospace", body: "\"IBM Plex Mono\", ui-monospace, monospace" },
  { id: "major", group: "mono", label: "major mono", hint: "Major Mono Display titles, text in Space Mono", display: "\"Major Mono Display\", ui-monospace, monospace", label2: "\"Space Mono\", ui-monospace, monospace", body: "\"Space Mono\", ui-monospace, monospace" },
  { id: "audiowide", group: "sci-fi", label: "audiowide", hint: "Audiowide titles over the original labels", display: "\"Audiowide\", system-ui, sans-serif", label2: "", body: "" },
  { id: "chakra", group: "sci-fi", label: "chakra petch", hint: "Chakra Petch, angular and techy", display: "\"Chakra Petch\", system-ui, sans-serif", label2: "\"Chakra Petch\", system-ui, sans-serif", body: "\"Chakra Petch\", system-ui, sans-serif" },
  { id: "unbounded", group: "sci-fi", label: "unbounded", hint: "Unbounded titles, Lexend text", display: "\"Unbounded\", system-ui, sans-serif", label2: "\"Lexend\", system-ui, sans-serif", body: "\"Lexend\", system-ui, sans-serif" },
  { id: "syne", group: "sci-fi", label: "syne", hint: "Syne, wide and art gallery edgy", display: "\"Syne\", system-ui, sans-serif", label2: "\"Syne\", system-ui, sans-serif", body: "\"Syne\", system-ui, sans-serif" },
  { id: "monoton", group: "retro", label: "monoton", hint: "Monoton titles, a neon sign outline", display: "\"Monoton\", system-ui, sans-serif", label2: "", body: "" },
  { id: "bungee", group: "retro", label: "bungee", hint: "Bungee titles, shop sign letters", display: "\"Bungee\", system-ui, sans-serif", label2: "", body: "" },
  { id: "righteous", group: "retro", label: "righteous", hint: "Righteous titles, a 70s poster face", display: "\"Righteous\", system-ui, sans-serif", label2: "", body: "" },
  { id: "bebas", group: "retro", label: "bebas", hint: "Bebas Neue titles, tall and flat", display: "\"Bebas Neue\", system-ui, sans-serif", label2: "", body: "" },
  { id: "pacifico", group: "retro", label: "pacifico", hint: "Pacifico titles, a surf shop script", display: "\"Pacifico\", \"Comic Sans MS\", cursive", label2: "", body: "" },
  { id: "fraunces", group: "serif", label: "fraunces", hint: "Fraunces, a soft old style serif", display: "\"Fraunces\", Georgia, serif", label2: "\"Fraunces\", Georgia, serif", body: "\"Fraunces\", Georgia, serif" },
  { id: "playfair", group: "serif", label: "playfair", hint: "Playfair Display, high contrast and elegant", display: "\"Playfair Display\", Georgia, serif", label2: "\"Playfair Display\", Georgia, serif", body: "\"Playfair Display\", Georgia, serif" },
  { id: "cormorant", group: "serif", label: "cormorant", hint: "Cormorant Garamond, a delicate book face", display: "\"Cormorant Garamond\", Georgia, serif", label2: "\"Cormorant Garamond\", Georgia, serif", body: "\"Cormorant Garamond\", Georgia, serif" },
  { id: "cinzel", group: "serif", label: "cinzel", hint: "Cinzel titles, carved roman capitals", display: "\"Cinzel\", Georgia, serif", label2: "", body: "\"Lora\", Georgia, serif" },
  { id: "marker", group: "handwritten", label: "marker", hint: "Permanent Marker titles, Gloria Hallelujah text", display: "\"Permanent Marker\", \"Comic Sans MS\", cursive", label2: "\"Gloria Hallelujah\", \"Comic Sans MS\", cursive", body: "\"Gloria Hallelujah\", \"Comic Sans MS\", cursive" },
  { id: "kalam", group: "handwritten", label: "kalam", hint: "Kalam, quick pen handwriting", display: "\"Kalam\", \"Comic Sans MS\", cursive", label2: "\"Kalam\", \"Comic Sans MS\", cursive", body: "\"Kalam\", \"Comic Sans MS\", cursive" },
  { id: "script", group: "handwritten", label: "script", hint: "Dancing Script titles, Kalam text", display: "\"Dancing Script\", \"Comic Sans MS\", cursive", label2: "\"Kalam\", \"Comic Sans MS\", cursive", body: "\"Kalam\", \"Comic Sans MS\", cursive" },
  { id: "indie", group: "handwritten", label: "indie flower", hint: "Indie Flower, loopy and cheerful", display: "\"Indie Flower\", \"Comic Sans MS\", cursive", label2: "\"Indie Flower\", \"Comic Sans MS\", cursive", body: "\"Indie Flower\", \"Comic Sans MS\", cursive" },
  { id: "shadows", group: "handwritten", label: "shadows", hint: "Shadows Into Light, thin felt tip", display: "\"Shadows Into Light\", \"Comic Sans MS\", cursive", label2: "\"Shadows Into Light\", \"Comic Sans MS\", cursive", body: "\"Shadows Into Light\", \"Comic Sans MS\", cursive" },
  { id: "nunito", group: "rounded", label: "nunito", hint: "Nunito, rounded and calm", display: "\"Nunito\", system-ui, sans-serif", label2: "\"Nunito\", system-ui, sans-serif", body: "\"Nunito\", system-ui, sans-serif" },
  { id: "quicksand", group: "rounded", label: "quicksand", hint: "Quicksand, light and round", display: "\"Quicksand\", system-ui, sans-serif", label2: "\"Quicksand\", system-ui, sans-serif", body: "\"Quicksand\", system-ui, sans-serif" },
  { id: "comfortaa", group: "rounded", label: "comfortaa", hint: "Comfortaa, circles and soft curves", display: "\"Comfortaa\", system-ui, sans-serif", label2: "\"Comfortaa\", system-ui, sans-serif", body: "\"Comfortaa\", system-ui, sans-serif" },
  { id: "baloo", group: "rounded", label: "baloo", hint: "Baloo 2, plump and friendly", display: "\"Baloo 2\", system-ui, sans-serif", label2: "\"Baloo 2\", system-ui, sans-serif", body: "\"Baloo 2\", system-ui, sans-serif" },
  { id: "lexend", group: "rounded", label: "lexend", hint: "Lexend, wide open and easy to read", display: "\"Lexend\", system-ui, sans-serif", label2: "\"Lexend\", system-ui, sans-serif", body: "\"Lexend\", system-ui, sans-serif" },
  { id: "josefin", group: "serif", label: "josefin", hint: "Josefin Sans, vintage geometric", display: "\"Josefin Sans\", system-ui, sans-serif", label2: "\"Josefin Sans\", system-ui, sans-serif", body: "\"Josefin Sans\", system-ui, sans-serif" },
  { id: "zenmaru", group: "japanese", label: "zen maru", hint: "Zen Maru Gothic, round gothic", display: "\"Zen Maru Gothic\", system-ui, sans-serif", label2: "\"Zen Maru Gothic\", system-ui, sans-serif", body: "\"Zen Maru Gothic\", system-ui, sans-serif" },
  { id: "yusei", group: "japanese", label: "yusei magic", hint: "Yusei Magic, felt pen marker", display: "\"Yusei Magic\", \"Comic Sans MS\", cursive", label2: "\"Yusei Magic\", \"Comic Sans MS\", cursive", body: "\"Yusei Magic\", \"Comic Sans MS\", cursive" },
  { id: "hachi", group: "japanese", label: "hachi maru pop", hint: "Hachi Maru Pop, bubbly pop lettering", display: "\"Hachi Maru Pop\", \"Comic Sans MS\", cursive", label2: "\"Hachi Maru Pop\", \"Comic Sans MS\", cursive", body: "\"Hachi Maru Pop\", \"Comic Sans MS\", cursive" },
  { id: "reggae", group: "japanese", label: "reggae one", hint: "Reggae One titles, heavy and loud", display: "\"Reggae One\", system-ui, sans-serif", label2: "", body: "" },
  { id: "creepster", group: "horror", label: "creepster", hint: "Creepster titles, dripping horror", display: "\"Creepster\", \"Comic Sans MS\", cursive", label2: "", body: "" },
  { id: "glitch", group: "horror", label: "glitch", hint: "Rubik Glitch titles, a broken signal", display: "\"Rubik Glitch\", system-ui, sans-serif", label2: "", body: "" }
];

export const GLASS: Choice[] = [
  { id: "clear", label: "clear glass", hint: "panels you can see through, with a blur" },
  { id: "frost", label: "frosted", hint: "half see through" },
  { id: "theme", label: "theme", hint: "whatever the theme does" },
  { id: "solid", label: "solid", hint: "fully opaque, easiest to read over a pattern" }
];

export const WIDTHS: Choice[] = [
  { id: "narrow", label: "narrow", hint: "about 820 px of content" },
  { id: "normal", label: "normal", hint: "about 1040 px of content" },
  { id: "wide", label: "wide", hint: "about 1360 px of content" },
  { id: "full", label: "full", hint: "use the whole screen" }
];

export const ALIGNS: Choice[] = [
  { id: "center", label: "centred", hint: "content in the middle of the screen" },
  { id: "left", label: "left", hint: "content next to the menu, spare room on the right" }
];

export const DENSITIES: Choice[] = [
  { id: "compact", label: "compact", hint: "tighter gaps, more on screen" },
  { id: "normal", label: "normal", hint: "the original spacing" },
  { id: "roomy", label: "roomy", hint: "extra air around everything" }
];

export const ENTERS: Choice[] = [
  { id: "blur", label: "blur in", hint: "pages sharpen into view" },
  { id: "fade", label: "fade", hint: "a plain fade" },
  { id: "slide", label: "slide up", hint: "pages rise into place" },
  { id: "none", label: "none", hint: "pages simply appear" }
];

export const BORDERS: Choice[] = [
  { id: "faint", label: "faint", hint: "barely there outlines" },
  { id: "normal", label: "normal", hint: "the original" },
  { id: "bold", label: "bold", hint: "stronger outlines" }
];

export const RAILS: Choice[] = [
  { id: "left", label: "left side", hint: "menu on the left" },
  { id: "right", label: "right side", hint: "menu on the right" }
];

export const DOCKS: Choice[] = [
  { id: "bottom", label: "bottom", hint: "radio dock at the bottom of the screen" },
  { id: "top", label: "top", hint: "radio dock at the top of the screen" }
];

export const CONTRASTS: Choice[] = [
  { id: "normal", label: "normal" },
  { id: "high", label: "high", hint: "brighter text and stronger lines everywhere" }
];

export const FOCUSES: Choice[] = [
  { id: "normal", label: "normal" },
  { id: "bold", label: "bold", hint: "a thick ring on whatever the keyboard has selected" }
];

export const SPACINGS: Choice[] = [
  { id: "normal", label: "normal" },
  { id: "loose", label: "loose", hint: "more space between letters, words and lines" }
];

// how panels, cards and tiles are framed. bracket is the original
export const PANELS: Choice[] = [
  { id: "bracket", label: "bracket", hint: "hud corner brackets, the original" },
  { id: "bevel", label: "bevel", hint: "raised like a hardware button, light top edge and dark bottom" },
  { id: "double", label: "double line", hint: "a thin line inside a thin line" },
  { id: "window", label: "window", hint: "a title bar strip across the top, like an old desktop" },
  { id: "paper", label: "paper", hint: "a light card with a slight tilt and a soft drop shadow" },
  { id: "offset", label: "offset", hint: "a hard shadow in your accent colour, like a sticker" },
  { id: "stitched", label: "stitched", hint: "a dashed seam just inside the edge" },
  { id: "none", label: "no frame", hint: "no outline at all, just the panel colour" }
];

// a layer over the whole screen. none is the original
export const FILTERS: Choice[] = [
  { id: "none", label: "none", hint: "the screen as it is" },
  { id: "crt", label: "crt", hint: "scanlines and dark corners, like a tube tv" },
  { id: "vhs", label: "vhs", hint: "colour fringes and tape noise lines" },
  { id: "bloom", label: "bloom", hint: "a soft glow around bright things" },
  { id: "halftone", label: "halftone", hint: "a comic print dot screen" },
  { id: "sepia", label: "sepia", hint: "warm old photo brown" },
  { id: "night", label: "night vision", hint: "green tint with a grainy scan" },
  { id: "dream", label: "dream", hint: "hazy, pastel and a little blurry" },
  { id: "gray", label: "grayscale", hint: "no colour at all" },
  { id: "glitch", label: "glitch", hint: "now and then the picture jitters and splits. calm motion keeps it still" }
];

// the cursor packs live in cursors.ts, written by scripts/make_cursors.mjs. mature ones are suggestive only
export const CURSORS: Choice[] = [{ id: "default", label: "system", hint: "your computer's own cursor" }, ...CURSOR_PACKS];

export interface LookPrefs {
  // "" keeps the theme's own, otherwise #rrggbb
  accent: string;
  bg: string;
  corners: string;
  font: string;
  glass: string;
  border: string;
  width: string;
  align: string;
  density: string;
  enter: string;
  rail: string;
  dock: string;
  contrast: string;
  focus: string;
  spacing: string;
  panel: string;
  filter: string;
  cursor: string;
  // adult themes and cursors are offered and kept only while this is on. suggestive, never explicit
  mature: boolean;
  // percent of what the theme does
  grain: number;
  fog: number;
  pattern: number;
  vignette: number;
  // how strong the screen filter is
  filterStrength: number;
  // whole interface size in percent
  scale: number;
  caps: boolean;
  glitch: boolean;
  // each station gets its own tint, moving backdrop and title decode
  worlds: boolean;
  // follow the device's light and dark setting, switching between two themes
  autoOn: boolean;
  autoLight: string;
  autoDark: string;
}

export const DEFAULT_LOOK: LookPrefs = {
  accent: "",
  bg: "",
  corners: "cut",
  font: "signal",
  glass: "theme",
  border: "normal",
  width: "normal",
  align: "center",
  density: "normal",
  enter: "blur",
  rail: "left",
  dock: "bottom",
  contrast: "normal",
  focus: "normal",
  spacing: "normal",
  panel: "bracket",
  filter: "none",
  cursor: "default",
  mature: false,
  grain: 100,
  fog: 100,
  pattern: 100,
  vignette: 100,
  filterStrength: 60,
  scale: 100,
  caps: true,
  glitch: true,
  worlds: true,
  autoOn: false,
  autoLight: "fog",
  autoDark: "night"
};

export interface SliderInfo {
  key: "grain" | "fog" | "pattern" | "vignette" | "filterStrength" | "scale";
  label: string;
  hint: string;
  min: number;
  max: number;
  step: number;
}

export const SLIDERS: SliderInfo[] = [
  { key: "grain", label: "film grain", hint: "moving speckle over everything", min: 0, max: 200, step: 10 },
  { key: "fog", label: "fog", hint: "the slow drifting haze behind the pages", min: 0, max: 100, step: 5 },
  { key: "pattern", label: "pattern strength", hint: "how strong the background pattern is, needs a pattern", min: 0, max: 150, step: 10 },
  { key: "vignette", label: "vignette", hint: "dark edges of the screen", min: 0, max: 100, step: 5 },
  { key: "filterStrength", label: "filter strength", hint: "how strong the screen filter is, needs a filter", min: 0, max: 100, step: 5 },
  { key: "scale", label: "interface size", hint: "makes everything bigger or smaller at once", min: 80, max: 160, step: 5 }
];

const hex = (value: unknown): string => (typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value.trim()) ? value.trim().toLowerCase() : "");
const oneOf = (value: unknown, list: Choice[], fallback: string): string => (typeof value === "string" && list.some((item) => item.id === value) ? value : fallback);
const num = (value: unknown, min: number, max: number, fallback: number): number => {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? Math.min(max, Math.max(min, Math.round(n))) : fallback;
};
const flag = (value: unknown, fallback: boolean): boolean => (typeof value === "boolean" ? value : fallback);
// a mature theme, cursor or preset only counts while this person has the switch on
const allowed = (item: { mature?: boolean } | undefined, mature: boolean): boolean => Boolean(item) && (mature || !item!.mature);
const oneOfAllowed = (value: unknown, list: Choice[], fallback: string, mature: boolean): string =>
  typeof value === "string" && allowed(list.find((item) => item.id === value), mature) ? value : fallback;
// unknown ids and mature themes without the switch fall back to the default, so a shared code cannot force them on
export function safeThemeId(value: unknown, mature: boolean, fallback = DEFAULT_THEME_ID): string {
  if (typeof value !== "string") return fallback;
  const theme = themeById(value);
  return allowed(theme, mature) ? theme.id : fallback;
}
const themeId = (value: unknown, fallback: string, mature: boolean): string => (typeof value === "string" ? safeThemeId(value, mature, fallback) : fallback);

export function cleanLook(raw: unknown): LookPrefs {
  const src = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const range = (key: SliderInfo["key"]) => {
    const slider = SLIDERS.find((item) => item.key === key)!;
    return num(src[key], slider.min, slider.max, DEFAULT_LOOK[key]);
  };
  const mature = flag(src.mature, DEFAULT_LOOK.mature);
  return {
    accent: hex(src.accent),
    bg: hex(src.bg),
    corners: oneOf(src.corners, CORNERS, DEFAULT_LOOK.corners),
    font: oneOf(src.font, FONTS, DEFAULT_LOOK.font),
    glass: oneOf(src.glass, GLASS, DEFAULT_LOOK.glass),
    border: oneOf(src.border, BORDERS, DEFAULT_LOOK.border),
    width: oneOf(src.width, WIDTHS, DEFAULT_LOOK.width),
    align: oneOf(src.align, ALIGNS, DEFAULT_LOOK.align),
    density: oneOf(src.density, DENSITIES, DEFAULT_LOOK.density),
    enter: oneOf(src.enter, ENTERS, DEFAULT_LOOK.enter),
    rail: oneOf(src.rail, RAILS, DEFAULT_LOOK.rail),
    dock: oneOf(src.dock, DOCKS, DEFAULT_LOOK.dock),
    contrast: oneOf(src.contrast, CONTRASTS, DEFAULT_LOOK.contrast),
    focus: oneOf(src.focus, FOCUSES, DEFAULT_LOOK.focus),
    spacing: oneOf(src.spacing, SPACINGS, DEFAULT_LOOK.spacing),
    panel: oneOf(src.panel, PANELS, DEFAULT_LOOK.panel),
    filter: oneOf(src.filter, FILTERS, DEFAULT_LOOK.filter),
    cursor: oneOfAllowed(src.cursor, CURSORS, DEFAULT_LOOK.cursor, mature),
    mature,
    grain: range("grain"),
    fog: range("fog"),
    pattern: range("pattern"),
    vignette: range("vignette"),
    filterStrength: range("filterStrength"),
    scale: range("scale"),
    caps: flag(src.caps, DEFAULT_LOOK.caps),
    glitch: flag(src.glitch, DEFAULT_LOOK.glitch),
    worlds: flag(src.worlds, DEFAULT_LOOK.worlds),
    autoOn: flag(src.autoOn, DEFAULT_LOOK.autoOn),
    autoLight: themeId(src.autoLight, DEFAULT_LOOK.autoLight, mature),
    autoDark: themeId(src.autoDark, DEFAULT_LOOK.autoDark, mature)
  };
}

// a whole look: what a preset, a saved look or a share code carries
export interface LookSnapshot {
  themeId: string;
  patternId: string;
  look: LookPrefs;
}

export interface SavedLook extends LookSnapshot {
  id: string;
  name: string;
}

export const MAX_SAVED_LOOKS = 24;

// mature overrides the switch the snapshot carries, so wearing someone else's look uses your own setting, never theirs
export function cleanSnapshot(raw: unknown, mature?: boolean): LookSnapshot {
  const src = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const rawLook = (src.look && typeof src.look === "object" ? src.look : {}) as Record<string, unknown>;
  const look = cleanLook(mature === undefined ? rawLook : { ...rawLook, mature });
  return {
    themeId: themeId(src.themeId, DEFAULT_THEME_ID, look.mature),
    patternId: typeof src.patternId === "string" ? patternById(src.patternId).id : DEFAULT_PATTERN_ID,
    look
  };
}

export function cleanSavedLooks(raw: unknown): SavedLook[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: SavedLook[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const { id, name } = item as Record<string, unknown>;
    if (typeof id !== "string" || !id || seen.has(id)) continue;
    seen.add(id);
    out.push({ id, name: String(typeof name === "string" && name.trim() ? name.trim() : "my look").slice(0, 40), ...cleanSnapshot(item) });
    if (out.length >= MAX_SAVED_LOOKS) break;
  }
  return out;
}

// ---------- presets ----------

export interface LookPreset {
  id: string;
  label: string;
  blurb: string;
  themeId: string;
  patternId: string;
  look: Partial<LookPrefs>;
  // adult vibe, only offered while the mature switch is on
  mature?: boolean;
}

// the other themes' ids in one place, so a rename only touches this block
const T = {
  otherworld: "rust", nurse: "hospital", y2k: "aqua", vhs: "vhs", zine: "zine", cassette: "cassette",
  moon: "moonlit", lime: "lime", rose: "quartz", sepia: "sepia", sea: "deepsea", arcade: "arcadenight",
  velvet: "velvet", boudoir: "boudoir", latex: "latex", lounge: "loungeneon", succubus: "succubus", honey: "honeytrap", cola: "cherrycola", hours: "afterhours"
};

export const LOOK_PRESETS: LookPreset[] = [
  { id: "default", label: "chi signal", blurb: "the original, untouched", themeId: "night", patternId: "none", look: {} },
  { id: "silent", label: "silent hill", blurb: "ember, heavy fog and grain", themeId: "ember", patternId: "rain", look: { grain: 170, fog: 100, vignette: 100, enter: "blur" } },
  { id: "flipnote", label: "flipnote", blurb: "sketch paper, handwriting, soft corners", themeId: "flipnote", patternId: "lined", look: { corners: "soft", font: "hand", glitch: false, grain: 60, caps: false } },
  { id: "memcard", label: "memory card", blurb: "psx grey, pixel type, sharp", themeId: "psx", patternId: "scan", look: { corners: "sharp", font: "pixel", border: "bold", enter: "none", fog: 30 } },
  { id: "magical", label: "magical girl", blurb: "lilac night, sparkles, round", themeId: "lilac", patternId: "stars", look: { corners: "round", glass: "frost", glitch: false, caps: false } },
  { id: "dsi", label: "dsi menu", blurb: "glossy white, rounded, clean", themeId: "dsi", patternId: "dots", look: { corners: "round", font: "clean", caps: false, grain: 0, glitch: false, enter: "slide" } },
  { id: "terminal", label: "terminal", blurb: "green crt, monospace, no frills", themeId: "phosphor", patternId: "scan", look: { corners: "sharp", font: "mono", grain: 120, fog: 20, enter: "none" } },
  { id: "matrix", label: "the rain", blurb: "code rain on black", themeId: "phosphor", patternId: "matrix", look: { corners: "sharp", font: "mono", glass: "clear", fog: 0, vignette: 100 } },
  { id: "cyber", label: "night city", blurb: "warning yellow, hazard stripes", themeId: "neon", patternId: "hazard", look: { corners: "cut", border: "bold", grain: 140, glitch: true } },
  { id: "cozy", label: "cozy afternoon", blurb: "peach cream and falling petals", themeId: "peach", patternId: "petals", look: { corners: "soft", glass: "frost", caps: false, glitch: false, grain: 50 } },
  { id: "midnight", label: "midnight forest", blurb: "moss and fireflies", themeId: "forest", patternId: "fireflies", look: { corners: "soft", fog: 100, font: "serif" } },
  { id: "vapor", label: "mall soft", blurb: "vapor pink and cyan edge", themeId: "vapor", patternId: "diamonds", look: { corners: "sharp", glass: "clear", border: "bold", font: "pixel" } },
  { id: "blueprint", label: "drafting table", blurb: "blueprint, grid and mono", themeId: "blueprint", patternId: "grid", look: { corners: "sharp", font: "mono", border: "bold", grain: 20 } },
  { id: "bsod", label: "blue screen", blurb: "a very polite crash", themeId: "bsod", patternId: "none", look: { corners: "sharp", font: "mono", border: "bold", grain: 20, fog: 40 } },
  { id: "deep", label: "deep sea", blurb: "abyss and slow bubbles", themeId: "abyss", patternId: "bubbles", look: { corners: "round", glass: "clear", fog: 100 } },
  { id: "easy", label: "easy reading", blurb: "big, clear text and strong contrast", themeId: "fog", patternId: "none", look: { font: "readable", contrast: "high", spacing: "loose", scale: 115, grain: 0, fog: 0, vignette: 0, glitch: false, enter: "fade", corners: "soft", caps: false, border: "bold", focus: "bold", glass: "solid" } },
  { id: "dyslexia", label: "dyslexia friendly", blurb: "cream paper, OpenDyslexic, calm", themeId: "flipnote", patternId: "none", look: { font: "dyslexia", spacing: "loose", scale: 110, grain: 0, fog: 0, vignette: 0, glitch: false, enter: "none", corners: "soft", caps: false, glass: "solid", focus: "bold" } },
  { id: "paper", label: "quiet paper", blurb: "light, calm, no effects", themeId: "fog", patternId: "none", look: { corners: "soft", font: "clean", caps: false, grain: 0, fog: 40, vignette: 30, glitch: false, enter: "fade" } },
  { id: "otherworld", label: "otherworld", blurb: "rust, static and a watching eye", themeId: T.otherworld, patternId: "scan", look: { corners: "cut", panel: "window", filter: "vhs", filterStrength: 50, cursor: "eye", grain: 150, fog: 100 } },
  { id: "nurse", label: "nurse station", blurb: "clinic green, clean windows, soft pulse", themeId: T.nurse, patternId: "cross", look: { corners: "soft", panel: "window", cursor: "heart", font: "clean", caps: false, glitch: false, grain: 20 } },
  { id: "bubble", label: "bubble os", blurb: "aqua gloss, bevels and bubbles", themeId: T.y2k, patternId: "bubbles", look: { corners: "round", panel: "bevel", glass: "clear", filter: "bloom", filterStrength: 40, cursor: "bubble", font: "rounded", caps: false, glitch: false } },
  { id: "fanzine", label: "fanzine", blurb: "photocopied paper, halftone, typewriter", themeId: T.zine, patternId: "dots", look: { corners: "sharp", panel: "paper", filter: "halftone", filterStrength: 40, cursor: "star", font: "typewriter", caps: false, glitch: false, grain: 90 } },
  { id: "walkman", label: "walkman", blurb: "tape deck chrome and a little wobble", themeId: T.cassette, patternId: "none", look: { corners: "soft", panel: "offset", filter: "vhs", filterStrength: 30, cursor: "cassette", font: "mono" } },
  { id: "arcade", label: "arcade cabinet", blurb: "attract mode, scanlines, pixel type", themeId: T.arcade, patternId: "scan", look: { corners: "sharp", panel: "double", filter: "crt", filterStrength: 70, cursor: "gamepad-dot", font: "arcade", border: "bold", enter: "none" } },
  { id: "moondiary", label: "moon diary", blurb: "violet night, stitched pages, dreamy haze", themeId: T.moon, patternId: "stars", look: { corners: "soft", panel: "stitched", filter: "dream", filterStrength: 40, cursor: "moon", font: "hand", caps: false, glitch: false } },
  { id: "fieldnotes", label: "field notes", blurb: "sepia paper, typewriter, pressed leaves", themeId: T.sepia, patternId: "lined", look: { corners: "sharp", panel: "paper", filter: "sepia", filterStrength: 50, cursor: "leaf", font: "typewriter", caps: false, glitch: false, grain: 80 } },
  { id: "velvet", label: "velvet room", blurb: "wine red, gold edges, slow bloom", themeId: T.velvet, patternId: "diamonds", mature: true, look: { corners: "soft", panel: "double", filter: "bloom", filterStrength: 50, cursor: "lips", font: "serif", caps: false, glitch: false } },
  { id: "latex", label: "latex noir", blurb: "black gloss, hard edges, handcuff cursor", themeId: T.latex, patternId: "none", mature: true, look: { corners: "sharp", panel: "bevel", glass: "clear", cursor: "handcuffs", border: "bold" } },
  { id: "afterhours", label: "after hours", blurb: "red light, tape noise, offset shadows", themeId: T.hours, patternId: "scan", mature: true, look: { corners: "cut", panel: "offset", filter: "vhs", filterStrength: 45, cursor: "red-light", grain: 130 } }
];

// a preset is built with the switch on when it is mature, so its own theme and cursor survive cleaning. the studio then
// puts the person's own switch back before wearing it
export function presetSnapshot(preset: LookPreset): LookSnapshot {
  return cleanSnapshot({ themeId: preset.themeId, patternId: preset.patternId, look: { ...DEFAULT_LOOK, ...preset.look } }, Boolean(preset.mature));
}

export function themeIds(): string[] {
  return THEMES.map((theme) => theme.id);
}

// ---------- share codes ----------

const CODE_PREFIX = "chixlook1.";

export function encodeLook(snapshot: LookSnapshot): string {
  const json = JSON.stringify({ themeId: snapshot.themeId, patternId: snapshot.patternId, look: snapshot.look });
  const bytes = new TextEncoder().encode(json);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return CODE_PREFIX + btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function decodeLook(code: string): LookSnapshot | null {
  const text = (code || "").trim();
  if (!text.startsWith(CODE_PREFIX)) return null;
  try {
    const body = text.slice(CODE_PREFIX.length).replace(/-/g, "+").replace(/_/g, "/");
    const binary = atob(body + "=".repeat((4 - (body.length % 4)) % 4));
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    // read as data with the switch on, the studio gates it against the wearer's own switch before it is painted
    return cleanSnapshot(JSON.parse(new TextDecoder().decode(bytes)), true);
  } catch {
    return null;
  }
}

// the look a snapshot becomes for someone whose mature switch is `mature`: their switch, their gating
export function wearSnapshot(snap: LookSnapshot, mature: boolean): LookSnapshot {
  return cleanSnapshot(snap, mature);
}
